// pages/Membership.js
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Typography,
  Container,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Paper,
  Fade,
  Backdrop,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TipForm from "../components/TipForm";

// Payment Success Modal Component
const PaymentSuccessModal = ({ open, onClose }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: (theme) => theme.zIndex.modal + 100, // Ensure it's above other content
      }}
    >
      <Fade in={open}>
        <Paper
          sx={{
            backgroundColor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxWidth: 400,
            mx: 2,
            outline: "none", // Remove focus outline
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 60, color: "success.main", mb: 2 }}
            />
            <Typography variant="h5" component="h2" gutterBottom>
              Thank You For Your Support!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your contribution has been received and is greatly appreciated.
              Explore our membership options below to enhance your diving
              experience even further!
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={onClose}
              sx={{ mt: 2 }}
            >
              Explore Memberships
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

function Membership() {
  const location = useLocation();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Check for payment success indicators when component mounts
  useEffect(() => {
    // Check URL parameters for payment success
    const queryParams = new URLSearchParams(location.search);
    const paymentSuccess = queryParams.get("paymentSuccess") === "true";

    // Check location state for showSuccessModal flag
    const stateShowModal = location.state?.showSuccessModal === true;

    // Show modal if either condition is true
    if (paymentSuccess || stateShowModal) {
      setShowSuccessModal(true);

      // Clean up URL parameters
      if (paymentSuccess) {
        const url = new URL(window.location.href);
        url.searchParams.delete("paymentSuccess");
        window.history.replaceState({}, "", url);
      }
    }
  }, [location]);

  // Handle closing the success modal
  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const tiers = [
    /* {
      title: "FREE",
      price: "$0",
      period: " perpetual",
      description:
        "Aimed at divers who want to know what divesites are out there.",
      features: [
        "3 day outlook for weather and tides",
        "Divesite list and map views",
        "Divesite overview",
        "Low resolution photos",
        "Create a divesite & log your dives",
      ],
      buttonText: "Register",
      buttonLink: "#",
      highlight: false,
    }, */
    {
      title: "Tip your DM & Crew",
      price: "$ - Your Choice",
      description: "A one-time contribution to support our operations",
      features: [
        "Support the community to cover basic operational costs.",
        "No commitment, just a one-time tip.",
      ],
      buttonText: "Say Thanks!",
      buttonLink: "/tip",
      highlight: false,
      isTipOption: true,
    }, /*
    {
      title: "Diver",
      price: "$3.50",
      period: "/month",
      description:
        "Perfect for knowing what's going on at your favorite divesites right now.",
      features: [
        "Save your 3 favorite dive sites",
        "3 day outlook for weather and tides",
        "Advanced search filters",
        "Badges",
        "Medium resolution photos",
        "Billed 3 months at a time",
      ],
      buttonText: "Subscribe to OWD",
      buttonLink: "#",
      highlight: false,
    },
    {
      title: "Advanced (option 1)",
      price: "$6.67",
      period: "/month",
      description:
        "Plan ahead for ideal conditions at your favorite divesites.",
      features: [
        "Save your 10 favorite dive sites",
        "7 day outlook for weather and tides",
        "Advanced search filters",
        "Medium resolution photos",
        "Diving and critter quests",
        "Badges",
        "Dashboard your favorites",
        "Billed 6 months at a time",
      ],
      buttonText: "Subscribe to AD",
      buttonLink: "#",
      highlight: true,
    },
    {
      title: "Advanced (option 2)",
      price: "$5",
      period: "/month",
      description:
        "Plan ahead for ideal conditions at your favorite divesites.",
      features: [
        "Save your 10 favorite dive sites",
        "7 day outlook for weather and tides",
        "Advanced search filters",
        "Medium resolution photos",
        "Diving and critter quests",
        "Badges",
        "Dashboard your favorites",
        "Billed 12 months at a time",
      ],
      buttonText: "Subscribe to AD",
      buttonLink: "#",
      highlight: true,
    },
    {
      title: "Master",
      price: "$6",
      period: "/month",
      description:
        "Pick based on best conditions at your regular sites. Enhanced features so you see new things",
      features: [
        "Save your 20 favorite dive sites",
        "7 day outlook for weather and tides",
        "Advanced search filters",
        "Diving and critter quests",
        "Badges",
        "Dashboard your favorites and dive history",
        "High resolution photos",
        "Include a depth profile for your dives",
        "Divebuddies",
        "Recommendations based on your dive history",
        "Eligible to become a community curator",
        "Billed 12 months at a time, when available",
      ],
      buttonText: "Join Waitlist",
      buttonLink: "#",
      highlight: false,
      comingSoon: true,
    },
    {
      title: "Director",
      price: "$10",
      period: "/month",
      description:
        "Comprehensive planning, best quality, recommendations, and offline mode.",
      features: [
        "Save your 50 favorite dive sites",
        "10 day outlook for weather and tides",
        "Advanced search filters",
        "Diving and critter quests",
        "Badges",
        "Divebuddies",
        "Organize your own dashboards",
        "Recommendations based on your dive history",
        "High resolution photos",
        "Include a depth profile for your dives",
        "Offline mode for your favorite dive sites",
        "Publish your profile and dive history",
        "Eligible to become a community curator",
        "Billed 12 months at a time, when available",
      ],
      buttonText: "Join Waitlist",
      buttonLink: "#",
      highlight: false,
      comingSoon: true,
    }, */
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
      {/* Payment Success Modal */}
      <PaymentSuccessModal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
      />

      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          Membership Plans
        </Typography>
        <Typography
          variant="h6"
          color="textSecondary"
          maxWidth="800px"
          mx="auto"
        >
          Join our community of divers and unlock premium features to get you
          out diving more often in better conditions. Choose the plan that fits
          your needs and visualize your dive better!
        </Typography>
      </Box>

      <Grid container spacing={3} justifyContent="center">
        {tiers.map((tier) => (
          <Grid
            item
            key={tier.title}
            xs={12}
            sm={tier.isTipOption ? 12 : 6}
            md={tier.isTipOption ? 12 : 6}
            lg={tier.isTipOption ? 12 : 4}
          >
            <Card
              elevation={tier.highlight ? 8 : 2}
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                borderRadius: 2,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: tier.isTipOption ? "none" : "translateY(-4px)",
                  boxShadow: tier.highlight ? 12 : 4,
                },
                ...(tier.highlight && {
                  border: "2px solid",
                  borderColor: "primary.main",
                }),
                ...(tier.isTipOption && {
                  overflow: "hidden",
                }),
              }}
            >
              {tier.highlight && (
                <Chip
                  label="POPULAR CHOICE"
                  color="primary"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    fontWeight: "bold",
                  }}
                />
              )}

              {tier.comingSoon && (
                <Chip
                  label="COMING SOON"
                  color="secondary"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    fontWeight: "bold",
                  }}
                />
              )}

              <CardContent sx={{ p: 3, flexGrow: 1 }}>
                <Typography
                  variant="h5"
                  component="h2"
                  fontWeight="bold"
                  gutterBottom
                >
                  {tier.title}
                </Typography>

                <Box sx={{ my: 2 }}>
                  <Typography
                    variant="h4"
                    component="span"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {tier.price}
                  </Typography>
                  {tier.period && (
                    <Typography
                      variant="subtitle1"
                      component="span"
                      color="text.secondary"
                    >
                      {tier.period}
                    </Typography>
                  )}
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  paragraph
                  sx={{ mb: 3 }}
                >
                  {tier.description}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <List disablePadding>
                  {tier.features.map((feature, index) => (
                    <ListItem key={index} disableGutters sx={{ py: 0.8 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: "body2" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>

              <CardActions sx={{ p: 3, pt: 0 }}>
                {tier.isTipOption ? (
                  // For tip tier, directly render the TipForm instead of a button
                  <Box sx={{ width: "100%" }}>
                    <TipForm />
                  </Box>
                ) : (
                  <Button
                    variant={tier.highlight ? "contained" : "outlined"}
                    color="primary"
                    fullWidth
                    size="large"
                    href={tier.buttonLink}
                    endIcon={tier.highlight ? <StarIcon /> : null}
                    sx={{ py: 1.2, borderRadius: 2, fontWeight: "medium" }}
                  >
                    {tier.buttonText}
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

export default Membership;
